import { useEffect, useState } from "react";
import { getJson } from "./http";

import { API_BASE } from "./apiBase";
import { GOOGLE_REVIEWS } from "./content";

/**
 * Phase 29 — live Google review rating + count for every review pill.
 *
 * The backend (`/api/reviews/google`) refreshes the numbers from the Google
 * Places API on a monthly cadence and caches them, so the pills across the
 * site stay current without a redeploy. Components start from the static
 * `GOOGLE_REVIEWS` values (no layout shift, works if the API is down) and
 * update once the live numbers arrive. Fetched once per page load and
 * shared by every pill.
 */

let current = { ...GOOGLE_REVIEWS, source: "static" };
let inflight = null;
const listeners = new Set();

/** Latest known values (static until the fetch resolves). Used by the
 *  JSON-LD builder so aggregateRating follows the live numbers too. */
export const currentGoogleReviews = () => current;

const isSane = (d) =>
  d && typeof d.rating === "number" && d.rating >= 1 && d.rating <= 5 &&
  Number.isInteger(d.count) && d.count >= 0;

export const loadGoogleReviews = () => {
  if (inflight) return inflight;
  inflight = getJson(`${API_BASE}/reviews/google`, { timeout: 8000 })
    .then(({ data }) => {
      if (isSane(data)) {
        current = {
          ...current,
          rating: data.rating,
          count: data.count,
          url: data.url || current.url,
          updatedAt: data.updatedAt || null,
          source: data.source || "api",
        };
        listeners.forEach((fn) => fn(current));
      }
      return current;
    })
    .catch(() => {
      // Don't pin a transient failure for the whole page lifetime: the next
      // pill to mount retries.
      inflight = null;
      return current;
    });
  return inflight;
};

export const useGoogleReviews = () => {
  const [reviews, setReviews] = useState(current);
  useEffect(() => {
    listeners.add(setReviews);
    loadGoogleReviews().then((v) => setReviews(v));
    return () => listeners.delete(setReviews);
  }, []);
  return reviews;
};
